import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import scoutProfileService from '../../services/scoutProfileService';
import teamService from '../../services/teamService';
import { currentSeasonYear } from '../../utils/season';

const initialState = {
  profile: null,
  loading: false,
  error: null,
  updateLoading: false,
  profileImageLoading: false,
  updateError: null,
  // Dashboard data
  dashboardData: null,
  dashboardLoading: false,
  dashboardError: null,
  suggestions: [],
  topPlayers: [],
  followedPlayers: [],
  // Uncommitted Tracker
  uncommittedPlayers: [],
  uncommittedPagination: {
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 50,
    hasMore: false,
  },
  uncommittedLoading: false,
  uncommittedError: null,
  // Statistics
  statisticsTopPlayers: [],
  statisticsPagination: {
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 50,
    hasMore: false,
  },
  statisticsLoading: false,
  statisticsError: null,
  // Followed Teams
  followedTeams: [],
  followedTeamsLoading: false,
  followedTeamsError: null,
  // All Teams
  scoutTeams: [],
  scoutTeamsLoading: false,
  scoutTeamsError: null,
  scoutTeamsPagination: {
    currentPage: 1,
    totalPages: 1,
    totalTeams: 0,
    limit: 10,
    hasMore: false,
  },
  // Team Roster
  teamRoster: [],
  teamRosterLoading: false,
  teamRosterError: null,
  selectedTeam: null,
  rosterTeamInfo: null,
  teamRosterPagination: {
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 50,
    hasMore: false,
  },
  // Scout Games
  scoutGames: [],
  scoutGamesLoading: false,
  scoutGamesError: null,
  scoutGamesPagination: {
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 10,
    hasMore: false,
  },
  // Dream Team Dashboard - Dual Rosters
  dreamTeamLeftRoster: [],
  dreamTeamRightRoster: [],
  dreamTeamLeftTeamId: null,
  dreamTeamRightTeamId: null,
  dreamTeamLeftRosterLoading: false,
  dreamTeamRightRosterLoading: false,
  dreamTeamRosterError: null,
};

export const getScoutProfile = createAsyncThunk(
  'scoutProfile/getProfile',
  async (_, { rejectWithValue }) => {
    try {
      const data = await scoutProfileService.getProfile();
      return data.scout || data.data?.scout || data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch scout profile.'
      );
    }
  }
);

export const updateScoutProfile = createAsyncThunk(
  'scoutProfile/updateProfile',
  async (profileData, { rejectWithValue }) => {
    try {
      const data = await scoutProfileService.updateProfile(profileData);
      return data.scout || data.data?.scout || data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update scout profile.'
      );
    }
  }
);

// Profile Image Actions
export const updateScoutProfileImage = createAsyncThunk(
  'scoutProfile/updateProfileImage',
  async (imageFile, { rejectWithValue }) => {
    try {
      const data = await scoutProfileService.updateProfileImage(imageFile);
      return data.scout || data.data?.scout || data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update profile image.'
      );
    }
  }
);

export const deleteScoutProfileImage = createAsyncThunk(
  'scoutProfile/deleteProfileImage',
  async (_, { rejectWithValue }) => {
    try {
      const data = await scoutProfileService.deleteProfileImage();
      return data.scout || data.data?.scout || data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to delete profile image.'
      );
    }
  }
);

// Dashboard Actions
export const getScoutDashboard = createAsyncThunk(
  'scoutProfile/getDashboard',
  async (params = { page: 1, limit: 20 }, { rejectWithValue }) => {
    try {
      const data = await scoutProfileService.getDashboard(params);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch dashboard data.'
      );
    }
  }
);

export const getScoutSuggestions = createAsyncThunk(
  'scoutProfile/getSuggestions',
  async (params = { limit: 10 }, { rejectWithValue }) => {
    try {
      const data = await scoutProfileService.getSuggestions(params);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch suggestions.'
      );
    }
  }
);

// Follow/Unfollow Actions
export const followPlayer = createAsyncThunk(
  'scoutProfile/followPlayer',
  async (playerId, { rejectWithValue }) => {
    try {
      const data = await scoutProfileService.followPlayer(playerId);
      return { playerId, ...data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to follow player.'
      );
    }
  }
);

export const unfollowPlayer = createAsyncThunk(
  'scoutProfile/unfollowPlayer',
  async (playerId, { rejectWithValue }) => {
    try {
      const data = await scoutProfileService.unfollowPlayer(playerId);
      return { playerId, ...data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to unfollow player.'
      );
    }
  }
);

// Uncommitted Tracker Actions
export const fetchUncommittedPlayers = createAsyncThunk(
  'scoutProfile/fetchUncommittedPlayers',
  async (params = { page: 1, limit: 10 }, { rejectWithValue }) => {
    try {
      const data = await scoutProfileService.getUncommittedPlayers(params);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch uncommitted players.'
      );
    }
  }
);

export const loadMoreUncommittedPlayers = createAsyncThunk(
  'scoutProfile/loadMoreUncommittedPlayers',
  async (params, { rejectWithValue }) => {
    try {
      const data = await scoutProfileService.getUncommittedPlayers(params);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to load more uncommitted players.'
      );
    }
  }
);

// Statistics Actions
export const fetchTopPlayers = createAsyncThunk(
  'scoutProfile/fetchTopPlayers',
  async (params = { limit: 10 }, { rejectWithValue }) => {
    try {
      const data = await scoutProfileService.getTopPlayers(params);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch top players.'
      );
    }
  }
);

// Follow/Unfollow Team Actions
export const followTeam = createAsyncThunk(
  'scoutProfile/followTeam',
  async (teamId, { rejectWithValue }) => {
    try {
      const data = await scoutProfileService.followTeam(teamId);
      return { teamId, ...data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to follow team.'
      );
    }
  }
);

export const unfollowTeam = createAsyncThunk(
  'scoutProfile/unfollowTeam',
  async (teamId, { rejectWithValue }) => {
    try {
      const data = await scoutProfileService.unfollowTeam(teamId);
      return { teamId, ...data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to unfollow team.'
      );
    }
  }
);

export const fetchFollowedTeams = createAsyncThunk(
  'scoutProfile/fetchFollowedTeams',
  async (params = { page: 1, limit: 20 }, { rejectWithValue }) => {
    try {
      const data = await scoutProfileService.getFollowedTeams(params.page, params.limit);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch followed teams.'
      );
    }
  }
);

// Scout Teams Actions
export const fetchScoutTeams = createAsyncThunk(
  'scoutProfile/fetchScoutTeams',
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await scoutProfileService.getScoutTeams(params);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch scout teams.'
      );
    }
  }
);

export const loadMoreScoutTeams = createAsyncThunk(
  'scoutProfile/loadMoreScoutTeams',
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await scoutProfileService.getScoutTeams(params);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to load more teams.'
      );
    }
  }
);

// Team Roster Actions
export const fetchTeamRoster = createAsyncThunk(
  'scoutProfile/fetchTeamRoster',
  async ({ teamId, page = 1, limit = 50, seasonYear = currentSeasonYear(), filters = null, sortBy = null, sortOrder = null, statsType = null, category = null, scope = null }, { rejectWithValue }) => {
    try {
      const data = await scoutProfileService.getTeamRoster(teamId, page, limit, seasonYear, filters, sortBy, sortOrder, statsType, category, scope);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch team roster.'
      );
    }
  }
);

export const loadMoreTeamRoster = createAsyncThunk(
  'scoutProfile/loadMoreTeamRoster',
  async ({ teamId, page, limit = 50, seasonYear = currentSeasonYear(), filters = null, sortBy = null, sortOrder = null, statsType = null, category = null, scope = null }, { rejectWithValue }) => {
    try {
      const data = await scoutProfileService.getTeamRoster(teamId, page, limit, seasonYear, filters, sortBy, sortOrder, statsType, category, scope);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to load more team roster.'
      );
    }
  }
);

// Scout Games Actions
export const fetchScoutGames = createAsyncThunk(
  'scoutProfile/fetchScoutGames',
  async (params = { page: 1, limit: 10 }, { rejectWithValue }) => {
    try {
      const data = await teamService.getScoutGames(params);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch scout games.'
      );
    }
  }
);

export const loadMoreScoutGames = createAsyncThunk(
  'scoutProfile/loadMoreScoutGames',
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await teamService.getScoutGames(params);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to load more scout games.'
      );
    }
  }
);

// Dream Team Roster Actions
export const fetchDreamTeamLeftRoster = createAsyncThunk(
  'scoutProfile/fetchDreamTeamLeftRoster',
  async ({ teamId, limit = 20 }, { rejectWithValue }) => {
    try {
      const data = await teamService.getScoutTeamRoster(teamId, 1, limit);
      return { teamId, ...data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch left team roster.'
      );
    }
  }
);

export const fetchDreamTeamRightRoster = createAsyncThunk(
  'scoutProfile/fetchDreamTeamRightRoster',
  async ({ teamId, limit = 20 }, { rejectWithValue }) => {
    try {
      const data = await teamService.getScoutTeamRoster(teamId, 1, limit);
      return { teamId, ...data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch right team roster.'
      );
    }
  }
);

const scoutProfileSlice = createSlice({
  name: 'scoutProfile',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.updateError = null;
    },
    clearProfile: (state) => {
      state.profile = null;
      state.error = null;
      state.updateError = null;
    },
    setSelectedTeam: (state, action) => {
      state.selectedTeam = action.payload;
    },
    clearTeamRoster: (state) => {
      state.teamRoster = [];
      state.selectedTeam = null;
      state.teamRosterPagination = {
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        limit: 50,
        hasMore: false,
      };
    },
    clearDreamTeamRosters: (state) => {
      state.dreamTeamLeftRoster = [];
      state.dreamTeamRightRoster = [];
      state.dreamTeamLeftTeamId = null;
      state.dreamTeamRightTeamId = null;
      state.dreamTeamRosterError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Scout Profile
      .addCase(getScoutProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getScoutProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload;
        state.error = null;
      })
      .addCase(getScoutProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Scout Profile
      .addCase(updateScoutProfile.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
      })
      .addCase(updateScoutProfile.fulfilled, (state, action) => {
        state.updateLoading = false;
        if (action.payload) state.profile = action.payload;
        state.updateError = null;
      })
      .addCase(updateScoutProfile.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload;
      })
      // Update Profile Image
      .addCase(updateScoutProfileImage.pending, (state) => {
        state.profileImageLoading = true;
        state.updateError = null;
      })
      .addCase(updateScoutProfileImage.fulfilled, (state, action) => {
        state.profileImageLoading = false;
        if (action.payload) state.profile = action.payload;
        state.updateError = null;
      })
      .addCase(updateScoutProfileImage.rejected, (state, action) => {
        state.profileImageLoading = false;
        state.updateError = action.payload;
      })
      // Delete Profile Image
      .addCase(deleteScoutProfileImage.pending, (state) => {
        state.profileImageLoading = true;
        state.updateError = null;
      })
      .addCase(deleteScoutProfileImage.fulfilled, (state, action) => {
        state.profileImageLoading = false;
        if (action.payload) state.profile = action.payload;
        state.updateError = null;
      })
      .addCase(deleteScoutProfileImage.rejected, (state, action) => {
        state.profileImageLoading = false;
        state.updateError = action.payload;
      })
      // Get Scout Dashboard
      .addCase(getScoutDashboard.pending, (state) => {
        state.dashboardLoading = true;
        state.dashboardError = null;
      })
      .addCase(getScoutDashboard.fulfilled, (state, action) => {
        state.dashboardLoading = false;
        const payload = action.payload;
        const data = payload.data || payload;
        state.dashboardData = data;
        state.suggestions = data.suggestions || payload.suggestions || [];
        state.topPlayers = data.topPlayers || payload.topPlayers || [];
        state.followedPlayers = data.followedPlayers || payload.followedPlayers || [];
        state.dashboardError = null;
      })
      .addCase(getScoutDashboard.rejected, (state, action) => {
        state.dashboardLoading = false;
        state.dashboardError = action.payload;
      })
      // Get Scout Suggestions
      .addCase(getScoutSuggestions.pending, (state) => {
        state.dashboardLoading = true;
        state.dashboardError = null;
      })
      .addCase(getScoutSuggestions.fulfilled, (state, action) => {
        state.dashboardLoading = false;
        state.suggestions = action.payload.suggestions || action.payload || [];
        state.dashboardError = null;
      })
      .addCase(getScoutSuggestions.rejected, (state, action) => {
        state.dashboardLoading = false;
        state.dashboardError = action.payload;
      })
      // Follow Player
      .addCase(followPlayer.pending, (state, action) => {
        state.error = null;
        const playerId = action.meta.arg;
        // Optimistically update in uncommitted players
        const uncommittedPlayer = state.uncommittedPlayers.find(p => p._id === playerId);
        if (uncommittedPlayer) {
          uncommittedPlayer.isFollowing = true;
        }
        // Also update in statistics top players
        const statsPlayer = state.statisticsTopPlayers.find(p => p._id === playerId);
        if (statsPlayer) {
          statsPlayer.isFollowing = true;
        }
      })
      .addCase(followPlayer.fulfilled, (state) => {
        state.error = null;
      })
      .addCase(followPlayer.rejected, (state, action) => {
        state.error = action.payload;
        const playerId = action.meta.arg;
        // Revert optimistic update
        const uncommittedPlayer = state.uncommittedPlayers.find(p => p._id === playerId);
        if (uncommittedPlayer) {
          uncommittedPlayer.isFollowing = false;
        }
        const statsPlayer = state.statisticsTopPlayers.find(p => p._id === playerId);
        if (statsPlayer) {
          statsPlayer.isFollowing = false;
        }
      })
      // Unfollow Player
      .addCase(unfollowPlayer.pending, (state, action) => {
        state.error = null;
        const playerId = action.meta.arg;
        // Optimistically update in uncommitted players
        const uncommittedPlayer = state.uncommittedPlayers.find(p => p._id === playerId);
        if (uncommittedPlayer) {
          uncommittedPlayer.isFollowing = false;
        }
        // Also update in statistics top players
        const statsPlayer = state.statisticsTopPlayers.find(p => p._id === playerId);
        if (statsPlayer) {
          statsPlayer.isFollowing = false;
        }
      })
      .addCase(unfollowPlayer.fulfilled, (state) => {
        state.error = null;
      })
      .addCase(unfollowPlayer.rejected, (state, action) => {
        state.error = action.payload;
        const playerId = action.meta.arg;
        // Revert optimistic update
        const uncommittedPlayer = state.uncommittedPlayers.find(p => p._id === playerId);
        if (uncommittedPlayer) {
          uncommittedPlayer.isFollowing = true;
        }
        const statsPlayer = state.statisticsTopPlayers.find(p => p._id === playerId);
        if (statsPlayer) {
          statsPlayer.isFollowing = true;
        }
      })
      // Fetch Uncommitted Players
      .addCase(fetchUncommittedPlayers.pending, (state) => {
        state.uncommittedLoading = true;
        state.uncommittedError = null;
      })
      .addCase(fetchUncommittedPlayers.fulfilled, (state, action) => {
        state.uncommittedLoading = false;
        const payload = action.payload;
        state.uncommittedPlayers = payload.players || payload.data?.players || (Array.isArray(payload.data) ? payload.data : []);
        const pag = payload.pagination || payload.data?.pagination || {};
        const currentPage = pag.currentPage || payload.currentPage || 1;
        const totalPages = pag.totalPages || payload.totalPages || 1;
        state.uncommittedPagination = {
          currentPage,
          totalPages,
          totalCount: pag.totalCount || payload.totalPlayers || payload.total || 0,
          limit: pag.limit || payload.limit || 50,
          hasMore: currentPage < totalPages,
        };
        state.uncommittedError = null;
      })
      .addCase(fetchUncommittedPlayers.rejected, (state, action) => {
        state.uncommittedLoading = false;
        state.uncommittedError = action.payload;
      })
      // Load More Uncommitted Players
      .addCase(loadMoreUncommittedPlayers.pending, (state) => {
        state.uncommittedLoading = true;
        state.uncommittedError = null;
      })
      .addCase(loadMoreUncommittedPlayers.fulfilled, (state, action) => {
        state.uncommittedLoading = false;
        const payload = action.payload;
        const newPlayers = payload.players || payload.data?.players || (Array.isArray(payload.data) ? payload.data : []);
        state.uncommittedPlayers = [...state.uncommittedPlayers, ...newPlayers];
        const pag = payload.pagination || payload.data?.pagination || {};
        state.uncommittedPagination = {
          currentPage: pag.currentPage || payload.currentPage || 1,
          totalPages: pag.totalPages || payload.totalPages || 1,
          totalCount: pag.totalCount || payload.totalPlayers || payload.total || 0,
          limit: pag.limit || payload.limit || 10,
          hasMore: pag.hasMore || false,
        };
        state.uncommittedError = null;
      })
      .addCase(loadMoreUncommittedPlayers.rejected, (state, action) => {
        state.uncommittedLoading = false;
        state.uncommittedError = action.payload;
      })
      // Fetch Top Players for Statistics
      .addCase(fetchTopPlayers.pending, (state) => {
        state.statisticsLoading = true;
        state.statisticsError = null;
      })
      .addCase(fetchTopPlayers.fulfilled, (state, action) => {
        state.statisticsLoading = false;
        const payload = action.payload;
        state.statisticsTopPlayers = payload.players || payload.data?.players || (Array.isArray(payload.data) ? payload.data : (Array.isArray(payload) ? payload : []));
        const pag = payload.pagination || payload.data?.pagination || {};
        const currentPage = pag.currentPage || payload.currentPage || 1;
        const totalPages = pag.totalPages || payload.totalPages || 1;
        state.statisticsPagination = {
          currentPage,
          totalPages,
          totalCount: pag.totalCount || payload.totalPlayers || payload.total || 0,
          limit: pag.limit || payload.limit || 50,
          hasMore: currentPage < totalPages,
        };
        state.statisticsError = null;
      })
      .addCase(fetchTopPlayers.rejected, (state, action) => {
        state.statisticsLoading = false;
        state.statisticsError = action.payload;
      })
      // Follow Team
      .addCase(followTeam.pending, (state) => {
        state.error = null;
      })
      .addCase(followTeam.fulfilled, (state) => {
        state.error = null;
      })
      .addCase(followTeam.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Unfollow Team
      .addCase(unfollowTeam.pending, (state) => {
        state.error = null;
      })
      .addCase(unfollowTeam.fulfilled, (state) => {
        state.error = null;
      })
      .addCase(unfollowTeam.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Fetch Followed Teams
      .addCase(fetchFollowedTeams.pending, (state) => {
        state.followedTeamsLoading = true;
        state.followedTeamsError = null;
      })
      .addCase(fetchFollowedTeams.fulfilled, (state, action) => {
        state.followedTeamsLoading = false;
        const payload = action.payload;
        state.followedTeams = payload.teams || payload.data?.teams || (Array.isArray(payload.data) ? payload.data : (Array.isArray(payload) ? payload : []));
        state.followedTeamsError = null;
      })
      .addCase(fetchFollowedTeams.rejected, (state, action) => {
        state.followedTeamsLoading = false;
        state.followedTeamsError = action.payload;
      })
      // Fetch Scout Teams
      .addCase(fetchScoutTeams.pending, (state) => {
        state.scoutTeamsLoading = true;
        state.scoutTeamsError = null;
      })
      .addCase(fetchScoutTeams.fulfilled, (state, action) => {
        state.scoutTeamsLoading = false;
        const payload = action.payload;
        state.scoutTeams = payload.teams || payload.data?.teams || (Array.isArray(payload.data) ? payload.data : (Array.isArray(payload) ? payload : []));
        const pagination = payload.pagination || payload.data?.pagination || {};
        state.scoutTeamsPagination = {
          currentPage: pagination.currentPage || 1,
          totalPages: pagination.totalPages || 1,
          totalTeams: pagination.totalTeams || pagination.totalCount || 0,
          limit: pagination.limit || 10,
          hasMore: pagination.currentPage < pagination.totalPages,
        };
        state.scoutTeamsError = null;
      })
      .addCase(fetchScoutTeams.rejected, (state, action) => {
        state.scoutTeamsLoading = false;
        state.scoutTeamsError = action.payload;
      })
      // Load More Scout Teams
      .addCase(loadMoreScoutTeams.pending, (state) => {
        state.scoutTeamsLoading = true;
        state.scoutTeamsError = null;
      })
      .addCase(loadMoreScoutTeams.fulfilled, (state, action) => {
        state.scoutTeamsLoading = false;
        const payload = action.payload;
        const newTeams = payload.teams || payload.data?.teams || (Array.isArray(payload.data) ? payload.data : (Array.isArray(payload) ? payload : []));
        state.scoutTeams = [...state.scoutTeams, ...newTeams];
        const pagination = payload.pagination || payload.data?.pagination || {};
        state.scoutTeamsPagination = {
          currentPage: pagination.currentPage || 1,
          totalPages: pagination.totalPages || 1,
          totalTeams: pagination.totalTeams || pagination.totalCount || 0,
          limit: pagination.limit || 10,
          hasMore: pagination.currentPage < pagination.totalPages,
        };
        state.scoutTeamsError = null;
      })
      .addCase(loadMoreScoutTeams.rejected, (state, action) => {
        state.scoutTeamsLoading = false;
        state.scoutTeamsError = action.payload;
      })
      // Fetch Team Roster
      .addCase(fetchTeamRoster.pending, (state) => {
        state.teamRosterLoading = true;
        state.teamRosterError = null;
      })
      .addCase(fetchTeamRoster.fulfilled, (state, action) => {
        state.teamRosterLoading = false;
        // Handle both direct { players: [...] } and nested { data: { players: [...] } } formats
        const payload = action.payload;
        const players = payload.players || payload.data?.players || payload.roster || (Array.isArray(payload.data) ? payload.data : []);
        state.teamRoster = players;
        state.rosterTeamInfo = payload.team || payload.data?.team || null;
        const pag = payload.pagination || payload.data?.pagination || {};
        state.teamRosterPagination = {
          currentPage: pag.currentPage || payload.currentPage || 1,
          totalPages: pag.totalPages || payload.totalPages || 1,
          totalCount: pag.totalCount || payload.totalPlayers || payload.total || 0,
          limit: pag.limit || payload.limit || 50,
          hasMore: pag.hasMore || false,
        };
        state.teamRosterError = null;
      })
      .addCase(fetchTeamRoster.rejected, (state, action) => {
        state.teamRosterLoading = false;
        state.teamRosterError = action.payload;
      })
      // Load More Team Roster
      .addCase(loadMoreTeamRoster.pending, (state) => {
        state.teamRosterLoading = true;
        state.teamRosterError = null;
      })
      .addCase(loadMoreTeamRoster.fulfilled, (state, action) => {
        state.teamRosterLoading = false;
        const payload = action.payload;
        const newPlayers = payload.players || payload.data?.players || payload.roster || (Array.isArray(payload.data) ? payload.data : []);
        state.teamRoster = [...state.teamRoster, ...newPlayers];
        const pag = payload.pagination || payload.data?.pagination || {};
        state.teamRosterPagination = {
          currentPage: pag.currentPage || payload.currentPage || 1,
          totalPages: pag.totalPages || payload.totalPages || 1,
          totalCount: pag.totalCount || payload.totalPlayers || payload.total || 0,
          limit: pag.limit || payload.limit || 50,
          hasMore: pag.hasMore || false,
        };
        state.teamRosterError = null;
      })
      .addCase(loadMoreTeamRoster.rejected, (state, action) => {
        state.teamRosterLoading = false;
        state.teamRosterError = action.payload;
      })
      // Fetch Scout Games
      .addCase(fetchScoutGames.pending, (state) => {
        state.scoutGamesLoading = true;
        state.scoutGamesError = null;
      })
      .addCase(fetchScoutGames.fulfilled, (state, action) => {
        state.scoutGamesLoading = false;
        const payload = action.payload;
        state.scoutGames = payload.games || payload.data?.games || (Array.isArray(payload.data) ? payload.data : []);
        const pag = payload.pagination || payload.data?.pagination || {};
        state.scoutGamesPagination = {
          currentPage: pag.currentPage || 1,
          totalPages: pag.totalPages || 1,
          totalCount: pag.totalCount || 0,
          limit: pag.limit || 10,
          hasMore: pag.hasMore || false,
        };
        state.scoutGamesError = null;
      })
      .addCase(fetchScoutGames.rejected, (state, action) => {
        state.scoutGamesLoading = false;
        state.scoutGamesError = action.payload;
      })
      // Load More Scout Games
      .addCase(loadMoreScoutGames.pending, (state) => {
        state.scoutGamesLoading = true;
        state.scoutGamesError = null;
      })
      .addCase(loadMoreScoutGames.fulfilled, (state, action) => {
        state.scoutGamesLoading = false;
        const payload = action.payload;
        const newGames = payload.games || payload.data?.games || (Array.isArray(payload.data) ? payload.data : []);
        state.scoutGames = [...state.scoutGames, ...newGames];
        const pag = payload.pagination || payload.data?.pagination || {};
        state.scoutGamesPagination = {
          currentPage: pag.currentPage || 1,
          totalPages: pag.totalPages || 1,
          totalCount: pag.totalCount || 0,
          limit: pag.limit || 10,
          hasMore: pag.hasMore || false,
        };
        state.scoutGamesError = null;
      })
      .addCase(loadMoreScoutGames.rejected, (state, action) => {
        state.scoutGamesLoading = false;
        state.scoutGamesError = action.payload;
      })
      // Fetch Dream Team Left Roster
      .addCase(fetchDreamTeamLeftRoster.pending, (state) => {
        state.dreamTeamLeftRosterLoading = true;
        state.dreamTeamRosterError = null;
      })
      .addCase(fetchDreamTeamLeftRoster.fulfilled, (state, action) => {
        state.dreamTeamLeftRosterLoading = false;
        const payload = action.payload;
        state.dreamTeamLeftRoster = payload.players || payload.data?.players || (Array.isArray(payload.data) ? payload.data : []);
        state.dreamTeamLeftTeamId = payload.teamId;
        state.dreamTeamRosterError = null;
      })
      .addCase(fetchDreamTeamLeftRoster.rejected, (state, action) => {
        state.dreamTeamLeftRosterLoading = false;
        state.dreamTeamRosterError = action.payload;
      })
      // Fetch Dream Team Right Roster
      .addCase(fetchDreamTeamRightRoster.pending, (state) => {
        state.dreamTeamRightRosterLoading = true;
        state.dreamTeamRosterError = null;
      })
      .addCase(fetchDreamTeamRightRoster.fulfilled, (state, action) => {
        state.dreamTeamRightRosterLoading = false;
        const payload = action.payload;
        state.dreamTeamRightRoster = payload.players || payload.data?.players || (Array.isArray(payload.data) ? payload.data : []);
        state.dreamTeamRightTeamId = payload.teamId;
        state.dreamTeamRosterError = null;
      })
      .addCase(fetchDreamTeamRightRoster.rejected, (state, action) => {
        state.dreamTeamRightRosterLoading = false;
        state.dreamTeamRosterError = action.payload;
      });
  },
});

export const { clearError, clearProfile, setSelectedTeam, clearTeamRoster, clearDreamTeamRosters } = scoutProfileSlice.actions;
export default scoutProfileSlice.reducer;
